import { useEffect, useState } from 'react'
import Modal from '../../components/Modal'
import Button from '../../components/Button'
import AccountSelect from '../../components/AccountSelect'
import { HelpTip } from '../../components/HelpTip'
import { useToast } from '../../components/Toast.jsx'
import { disposeAsset } from '../../api/operatingCosts'
import { localToday } from '../../utils/format'
import { money, num } from '../sales/salesShared'

// Take an asset off the books: its cost and accumulated depreciation come
// out, money received goes to the account chosen, and the difference from
// the book value is a gain or a loss. Accounts are always chosen, never
// assumed.
export default function DisposeAssetModal({ open, asset, onClose, onDisposed }) {
  const { notify } = useToast()
  const [date, setDate] = useState(localToday())
  const [proceeds, setProceeds] = useState('')
  const [proceedsCode, setProceedsCode] = useState('')
  const [gainLossCode, setGainLossCode] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setDate(localToday()); setProceeds(''); setProceedsCode(''); setGainLossCode(''); setNotes(''); setError('')
  }, [open])

  if (!open || !asset) return null
  const nbv = num(asset.net_book_value)
  const received = num(proceeds)
  const diff = Math.round((received - nbv) * 100) / 100

  const dispose = async () => {
    setError('')
    if (!date) { setError('Enter the date.'); return }
    if (received < 0) { setError('What was received can’t be negative.'); return }
    if (received > 0 && !proceedsCode) { setError('Choose the account the money went to.'); return }
    if (diff !== 0 && !gainLossCode) { setError(`Choose the ${diff > 0 ? 'gain' : 'loss'} on disposal account.`); return }
    setSaving(true)
    try {
      await disposeAsset(asset.asset_id, {
        disposal_date: date,
        proceeds: received,
        ...(received > 0 ? { proceeds_account_code: proceedsCode } : {}),
        ...(diff !== 0 ? { gain_loss_account_code: gainLossCode } : {}),
        notes: notes.trim(),
      })
      notify(`${asset.name} disposed`)
      onDisposed?.()
      onClose?.()
    } catch (e) {
      setError(e.message || 'Could not dispose of the asset.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!saving) onClose?.() }}
      title={<>Dispose of {asset.name} <HelpTip k="disposal" /></>}
      subtitle="Sold, scrapped or given away. This can’t be undone here."
      preventClose={saving}
      footer={(
        <>
          <Button variant="ghost" onClick={() => onClose?.()} disabled={saving}>Cancel</Button>
          <Button variant="danger" onClick={dispose} loading={saving}>Dispose</Button>
        </>
      )}
    >
      {error && <p className="acc-modal-error">{error}</p>}
      <div className="acc-modal-stack">
        <dl className="acc-details acc-details-tight">
          <div><dt>Book value</dt><dd>{money(nbv)}</dd></div>
          <div><dt>{diff < 0 ? 'Loss' : 'Gain'}</dt><dd className={diff < 0 ? 'acc-neg' : undefined}>{money(Math.abs(diff))}</dd></div>
        </dl>
        <div className="acc-two-fields">
          <label className="ci-form-field">
            <span>Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="ci-form-field">
            <span>Received for it</span>
            <input value={proceeds} onChange={(e) => setProceeds(e.target.value)} inputMode="decimal" placeholder="0.00" />
          </label>
        </div>
        {received > 0 && (
          <label className="ci-form-field">
            <span>Money went to</span>
            <AccountSelect value={proceedsCode} onChange={setProceedsCode} types={['Asset']} />
          </label>
        )}
        {diff !== 0 && (
          <label className="ci-form-field">
            <span>{diff > 0 ? 'Gain on disposal account' : 'Loss on disposal account'}</span>
            <AccountSelect value={gainLossCode} onChange={setGainLossCode} types={['Revenue', 'Expense']} />
          </label>
        )}
        <label className="ci-form-field">
          <span>Notes (optional)</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Sold to a staff member" />
        </label>
      </div>
    </Modal>
  )
}
